import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, LOGO_URL } from "@/lib/api";
import { Field } from "@/components/vaulted";

const DISCORD_ERRORS = {
  discord_not_configured: "Discord login isn't configured yet. Please use email for now.",
  discord_cancelled: "Discord login was cancelled.",
  discord_state_invalid: "Your Discord login session expired. Please try again.",
  discord_email_exists: "An account with your Discord email already exists. Log in with email, then link Discord in Account Settings.",
  discord_email_unverified: "Your Discord account needs a verified email address.",
  discord_token_failed: "Discord rejected the login request. Please try again.",
  account_suspended: "This account has been suspended.",
};

const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.6 1.3a18.4 18.4 0 0 0-5.6 0L8.6 3a19.7 19.7 0 0 0-4.9 1.5C.6 9.1-.3 13.6.1 18.1a19.9 19.9 0 0 0 6 3l1.3-2.1a12.8 12.8 0 0 1-2-1l.5-.4a14.2 14.2 0 0 0 12.2 0l.5.4c-.6.4-1.3.7-2 1l1.3 2.1a19.8 19.8 0 0 0 6-3c.5-5.2-.8-9.7-3.6-13.7ZM8 15.4c-1.2 0-2.2-1.1-2.2-2.4S6.8 10.6 8 10.6s2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Zm8 0c-1.2 0-2.2-1.1-2.2-2.4s1-2.4 2.2-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Z" /></svg>
);

export const DiscordButton = ({ label = "Continue with Discord", mode = "login", testId = "discord-login-button" }) => {
  const href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/login?mode=${mode}`;
  const onClick = async (e) => {
    if (mode !== "link") return;
    e.preventDefault();
    try { await api.get("/auth/me"); } catch {}
    window.location.href = href;
  };
  return (
    <a href={href} onClick={onClick} data-testid={testId}
      className="v-btn h-10 w-full bg-[#5865F2] text-white hover:bg-[#4752c4]"><DiscordIcon />{label}</a>
  );
};

function AuthShell({ title, subtitle, children, testId }) {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] hero-bg" data-testid={testId}>
      <div className="absolute inset-0 grid-lines" />
      <div className="container-v relative flex justify-center py-14">
        <div className="w-full max-w-md v-panel p-7 rise shadow-[0_0_60px_-30px_rgba(139,92,246,0.6)]">
          <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-full ring-1 ring-violet-500/40" />
          <h1 className="mt-5 text-xl font-semibold text-white">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

const Divider = () => <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-zinc-600"><span className="h-px flex-1 bg-[#27272A]" />or<span className="h-px flex-1 bg-[#27272A]" /></div>;
const ErrorBox = ({ msg, testId }) => msg ? <div data-testid={testId} className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{msg}</div> : null;

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(DISCORD_ERRORS[params.get("error")] || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); nav(params.get("next") || "/account"); }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Welcome back" subtitle="Log in to your Vaulted account." testId="login-page">
      <ErrorBox msg={err} testId="login-error" />
      <DiscordButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email"><input data-testid="login-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="v-input" /></Field>
        <Field label="Password"><input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="v-input" /></Field>
        <div className="flex justify-end"><Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs v-link">Forgot password?</Link></div>
        <button data-testid="login-submit-button" disabled={busy} className="v-btn-primary w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Log in</button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">New to Vaulted? <Link to="/register" data-testid="login-register-link" className="text-violet-300 hover:text-violet-200">Create an account</Link></p>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", email: "", password: "", confirm_password: "", accept_terms: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm_password) return setErr("Passwords do not match");
    if (!f.accept_terms) return setErr("You must accept the Terms of Service");
    setBusy(true);
    try { await register(f); toast.success("Account created", { description: "Check your inbox to verify your email." }); nav("/account"); }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <AuthShell title="Create your account" subtitle="Buy from independent creators or start selling." testId="register-page">
      <ErrorBox msg={err} testId="register-error" />
      <DiscordButton label="Sign up with Discord" testId="discord-register-button" />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Username" hint="3–24 characters: letters, numbers, _ . -"><input data-testid="register-username-input" required value={f.username} onChange={set("username")} className="v-input" /></Field>
        <Field label="Email"><input data-testid="register-email-input" type="email" required value={f.email} onChange={set("email")} className="v-input" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Password"><input data-testid="register-password-input" type="password" required minLength={8} value={f.password} onChange={set("password")} className="v-input" /></Field>
          <Field label="Confirm Password"><input data-testid="register-confirm-input" type="password" required value={f.confirm_password} onChange={set("confirm_password")} className="v-input" /></Field>
        </div>
        <p className="text-[11px] text-zinc-500">At least 8 characters with a letter and a number.</p>
        <label className="flex items-start gap-2 text-sm text-zinc-400 cursor-pointer">
          <input data-testid="register-terms-checkbox" type="checkbox" checked={f.accept_terms} onChange={set("accept_terms")} className="mt-0.5 accent-violet-500" />
          <span>I agree to the <Link to="/legal/terms" className="text-violet-300 hover:underline">Terms of Service</Link> and <Link to="/legal/privacy" className="text-violet-300 hover:underline">Privacy Policy</Link>.</span>
        </label>
        <button data-testid="register-submit-button" disabled={busy} className="v-btn-primary w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create account</button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">Already have an account? <Link to="/login" className="text-violet-300 hover:text-violet-200">Log in</Link></p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setDone(true); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a secure reset link." testId="forgot-page">
      <ErrorBox msg={err} />
      {done ? <p data-testid="forgot-success" className="text-sm text-emerald-400">If an account exists for that email, a reset link has been sent. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="v-input" /></Field>
          <button data-testid="forgot-submit-button" className="v-btn-primary w-full">Send reset link</button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="v-link">Back to login</Link></p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.a !== pw.b) return setErr("Passwords do not match");
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw.a }); toast.success("Password updated. Please log in."); nav("/login"); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthShell title="Choose a new password" testId="reset-page">
      <ErrorBox msg={err} testId="reset-error" />
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password"><input data-testid="reset-password-input" type="password" required minLength={8} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="v-input" /></Field>
        <Field label="Confirm password"><input data-testid="reset-confirm-input" type="password" required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className="v-input" /></Field>
        <button data-testid="reset-submit-button" className="v-btn-primary w-full">Update password</button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading");
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") || "" }).then(() => { setState("ok"); refresh(); }).catch(() => setState("fail"));
  }, []); // eslint-disable-line
  return (
    <AuthShell title="Email verification" testId="verify-page">
      {state === "loading" && <Loader2 className="h-6 w-6 animate-spin text-violet-400" />}
      {state === "ok" && <p data-testid="verify-success" className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" />Your email is verified.</p>}
      {state === "fail" && <p data-testid="verify-fail" className="flex items-center gap-2 text-sm text-red-400"><XCircle className="h-4 w-4" />This link is invalid or has expired.</p>}
      <Link to="/account" className="mt-6 v-btn-ghost w-full">Go to dashboard</Link>
    </AuthShell>
  );
}
